import { useI18n } from 'vue-i18n';

/**
 * Composable providing centralized status mappings for
 * service order statuses and service order item statuses.
 */
export function useSOStatusMap() {
  const { t } = useI18n();

  const orderStatusMap = {
    draft: { label: t('Em Caixa'), severity: 'warning', icon: 'pi-shopping-cart' },
    aguardando_laudos_entrada: { label: t('Aguardando Laudos'), severity: 'info', icon: 'pi-camera' },
    em_producao: { label: t('Em Produção'), severity: 'info', icon: 'pi-cog' },
    aguardando_laudos_saida: { label: t('Laudos de Saída'), severity: 'info', icon: 'pi-camera' },
    pronto_para_entrega: { label: t('Pronto'), severity: 'success', icon: 'pi-check-circle' },
    parcialmente_entregue: { label: t('Parcial'), severity: 'warning', icon: 'pi-truck' },
    entregue: { label: t('Entregue'), severity: 'success', icon: 'pi-verified' },
    cancelled: { label: t('Cancelada'), severity: 'danger', icon: 'pi-times-circle' }
  };

  const itemStatusMap = {
    aguardando_laudo_entrada: { label: t('Aguardando Laudo'), severity: 'info', icon: 'pi-camera' },
    em_producao: { label: t('Em Produção'), severity: 'info', icon: 'pi-cog' },
    aguardando_laudo_saida: { label: t('Laudo de Saída'), severity: 'info', icon: 'pi-camera' },
    pronto: { label: t('Pronto'), severity: 'success', icon: 'pi-check-circle' },
    entregue: { label: t('Entregue'), severity: 'success', icon: 'pi-verified' }
  };

  const getOrderStatus = (status: any) => (orderStatusMap as any)[status] || { label: status, severity: 'secondary', icon: 'pi-question' };
  const getItemStatus = (status: any) => (itemStatusMap as any)[status] || { label: status, severity: 'secondary', icon: 'pi-question' };

  const financialStatusMap = {
    a_receber: { label: t('A receber'), severity: 'danger' },
    recebimento_parcial: { label: t('Parcial'), severity: 'warning' },
    recebido: { label: t('Recebido'), severity: 'success' }
  };

  const getFinancialStatus = (status: any) => (financialStatusMap as any)[status] || { label: status ?? '-', severity: 'secondary' };

  const prazoSeverityMap: Record<string, string> = {
    atrasado_pronto: 'danger',
    atrasado: 'danger',
    avisar: 'warning',
    aguardando_cliente: 'info',
    proximo_do_prazo: 'info',
    no_prazo: 'secondary'
  };

  const prazoSeverity = (label: any) => prazoSeverityMap[label] || 'secondary';

  const prazoLabelMap: Record<string, string> = {
    no_prazo: t('No prazo'),
    proximo_do_prazo: t('Próximo do prazo'),
    aguardando_cliente: t('Aguardando cliente'),
    avisar: t('Avisar'),
    atrasado: t('Atrasado'),
    atrasado_pronto: t('Atrasado pronto')
  };

  const prazoLabel = (label: any) => prazoLabelMap[label] ?? label;

  const daysUntilDueText = (days: number | null | undefined): { text: string; severity: string } => {
    if (days == null) return { text: '—', severity: 'secondary' };
    if (days < 0) return { text: t('{n} dia(s) atrasado', { n: Math.abs(days) }), severity: 'danger' };
    if (days === 0) return { text: t('Hoje'), severity: 'warning' };
    return { text: t('Em {d} dia(s)', { d: days }), severity: 'info' };
  };

  return {
    orderStatusMap,
    itemStatusMap,
    getOrderStatus,
    getItemStatus,
    financialStatusMap,
    getFinancialStatus,
    prazoSeverity,
    prazoLabel,
    daysUntilDueText
  };
}
